/**
 * Sidebar — RTL-first navigation rail mirroring `design_handoff/mvp/_shell.html`.
 *
 * Section visibility binds to the actor's roles + permissions per the
 * matrix in `docs/frontend/035-frontend-app-shell-plan.md §5.1`. Sections
 * the actor cannot read at all are HIDDEN, not disabled
 * (`docs/13-frontend-integration-rules.md §3` — "hide actions the user
 * should not know exist"). Sysadmin collapses to a banner-only sidebar
 * per §5.4.
 */

import { NavLink, useLocation } from 'react-router-dom';
import type { ReactNode } from 'react';
import { useMeContext } from '../../context/MeContextProvider';
import { useClientScope } from '../../context/ClientScopeProvider';
import {
  hasActiveClientAssignment,
  hasAnyPermission,
  hasClientAssignment,
  hasRoleCategory,
  isAgencyProductionOnly,
  isClientOnly,
  isLeadership,
  roleLabelAr,
  PERM,
} from '../../permissions';
import {
  BellIcon,
  ClientsIcon,
  ContentIcon,
  CreativeIcon,
  CrmIcon,
  HomeIcon,
  InfluencersIcon,
  OverviewIcon,
  PaidMediaIcon,
  PublishIcon,
  ReportsIcon,
  SettingsIcon,
  StrategyIcon,
  TeamIcon,
} from './icons';
import { ClientSwitcher } from './ClientSwitcher';

const INFLUENCER_SURFACES_ENABLED = false;

interface SideItemProps {
  readonly to: string;
  readonly icon: ReactNode;
  readonly children: ReactNode;
  readonly count?: number | string;
  readonly hasUpdates?: boolean;
  readonly end?: boolean;
}

function SideItem({ to, icon, children, count, hasUpdates, end }: SideItemProps) {
  return (
    <NavLink
      to={to}
      end={end}
      className={({ isActive }) => (isActive ? 'side-item active' : 'side-item')}
    >
      {icon}
      <span style={{ flex: 1 }}>{children}</span>
      {typeof count !== 'undefined' ? <span className="count">{count}</span> : null}
      {hasUpdates ? <span className="badge-dot" /> : null}
    </NavLink>
  );
}

function SectionLabel({ children }: { readonly children: ReactNode }) {
  return <span className="side-label">{children}</span>;
}

function getInitials(name: string | null | undefined): string {
  if (!name) return '؟';
  const parts = name
    .split(/\s+/)
    .filter((p) => p.length > 0)
    .slice(0, 2);
  if (parts.length === 0) return name.charAt(0);
  return parts.map((p) => p.charAt(0)).join('');
}

export function Sidebar() {
  const { me, signOut } = useMeContext();
  const { clients, leadership, selectedClientId } = useClientScope();
  const location = useLocation();

  if (!me) return null;

  const sysadmin = me.system_admin;
  // Task 303-A-B — `roleClientOnly` is the ROLE-derived answer. `clientOnly`
  // below widens it with the continuity shape, because `isClientOnly` reads
  // `me.roles` and that array is EMPTY for a client of a non-operational
  // tenant: the actor genuinely is client-only, the roles read simply cannot
  // say so. Without this the agency group opens for a client user —
  // «الرئيسية» and «العملاء», the second of which reads «لا يوجد عملاء بعد»
  // to a client whose agency has plenty.
  const roleClientOnly = isClientOnly(me);
  // Task 118 — Employee Workspace v1. Production-execute staff
  // (designer / video_editor / etc., NOT leadership and NOT
  // sysadmin) get a focused 5-item sidebar. The "agency-admin" group
  // (CRM, reports, paid media, services, team management, knowledge
  // library) is hidden because their role has no permission to use
  // any of it; the per-client workspace group is hidden because
  // their work entry-point is the dedicated /app/me/tasks queue, not
  // an org-wide client browse.
  const productionOnly = isAgencyProductionOnly(me);
  const perms = me.permissions;
  const activeClientId =
    selectedClientId ??
    (!productionOnly && typeof window !== 'undefined'
      ? window.localStorage.getItem('kmi:lastClientId')
      : null);
  const activeClient = activeClientId
    ? (clients.find((c) => c.id === activeClientId) ?? null)
    : null;

  // Administrative workspaces restore the last client after navigation. Production
  // queues use only the explicit URL filter so "all tasks" cannot look selected.
  if (!productionOnly && typeof window !== 'undefined' && selectedClientId) {
    window.localStorage.setItem('kmi:lastClientId', selectedClientId);
  }

  // Task 118 — Employee Workspace v1. Production-only staff get a
  // focused 5-item rail (الرئيسية/مهامي, العملاء, مكتبة الإبداع,
  // الإشعارات, الإعدادات) and the leadership/admin surfaces are
  // hidden entirely. Same UI-only posture as `clientOnly`: RLS is
  // the load-bearing security gate; this just shapes the nav.
  if (productionOnly) {
    const showCreativeMine = hasAnyPermission(perms, PERM.CREATIVE_INTELLIGENCE_READ);
    const showNotificationsMine = hasAnyPermission(perms, PERM.NOTIFICATION_READ);
    // Task 292C3A — expose the canonical StrategyPage read-surface to
    // eligible writer roles inside their focused Employee Workspace rail.
    // Load-bearing gate is RLS (`app.has_active_client_assignment`); the
    // FE mirrors it so the link does not render for a stale me-context
    // whose selected client just flipped to onboarding / paused /
    // archived. Points at the ACTIVE client's canonical StrategyPage —
    // no bespoke writer strategy UI.
    const showWriterStrategy =
      hasAnyPermission(perms, PERM.STRATEGY_READ, PERM.STRATEGY_READ_ASSIGNED_ACTIVE) &&
      activeClientId !== null &&
      hasActiveClientAssignment(me, activeClientId);
    return (
      <aside className="side">
        <a className="side-logo" href="/app">
          super<em>agency</em>
        </a>
        <ClientSwitcher activeClientId={activeClientId} />
        <div className="side-group">
          <SectionLabel>الرئيسية</SectionLabel>
          <SideItem to="/app/me/tasks" icon={<HomeIcon />}>
            مهامي
          </SideItem>
          <SideItem to="/app/clients" icon={<ClientsIcon />} count={clients.length || undefined}>
            العملاء
          </SideItem>
          {showWriterStrategy ? (
            <SideItem to={`/app/clients/${activeClientId}/strategy`} icon={<StrategyIcon />}>
              الاستراتيجية
            </SideItem>
          ) : null}
          {showCreativeMine ? (
            <SideItem to="/app/creative" icon={<CreativeIcon />}>
              مكتبة الإبداع
            </SideItem>
          ) : null}
          {showNotificationsMine ? (
            <SideItem to="/app/notifications" icon={<BellIcon />}>
              الإشعارات
            </SideItem>
          ) : null}
          <SideItem to="/app/settings" icon={<SettingsIcon />}>
            الإعدادات
          </SideItem>
        </div>
        <div className="side-footer">
          <span className="av">
            {getInitials(me.profile?.full_name ?? me.profile?.email ?? null)}
          </span>
          <span className="side-me">
            <span className="nm">{me.profile?.full_name ?? me.profile?.email ?? 'مستخدم'}</span>
            <span className="rl">
              {me.roles
                .filter((r) => r.category === 'agency')
                .slice(0, 2)
                .map((r) => (
                  <span key={r.key} className="role-pill">
                    {roleLabelAr(r.key)}
                  </span>
                ))}
            </span>
          </span>
          <button
            type="button"
            className="btn-text"
            onClick={() => {
              void signOut();
            }}
            title="تسجيل الخروج"
            style={{ padding: 6 }}
          >
            خروج
          </button>
        </div>
      </aside>
    );
  }

  const showStrategy = !sysadmin && hasAnyPermission(perms, PERM.STRATEGY_READ);
  const showContent = !sysadmin && hasAnyPermission(perms, PERM.CONTENT_READ);
  const showPublishing = !sysadmin && hasAnyPermission(perms, PERM.PUBLISH_CONNECTION_READ);
  const showPaidMedia =
    !sysadmin &&
    hasAnyPermission(perms, PERM.PAID_MEDIA_ACCOUNT_READ, PERM.PAID_MEDIA_CAMPAIGN_READ);
  // Task 144 — client-workspace paid-media link relaxes the perm gate
  // for client-only users with an active assignment to the active
  // client. Without this, client-tier users (who don't carry
  // `paid_media.*.read`) have no discoverable path to
  // `/app/clients/<id>/paid-media` even when the package unlocks
  // paid-media — and therefore can't reach the Task 141 IC-3 Meta Ads
  // connect CTA. The Sidebar does NOT fetch package capabilities; the
  // ClientWorkspaceLayout's `CapabilityRouteGuard` is the load-bearing
  // page-level gate for locked packages. The agency-section item on
  // line ~283 below keeps the original `showPaidMedia` perm-only gate.
  const showPaidMediaClient =
    !sysadmin &&
    (showPaidMedia ||
      (activeClientId !== null && isClientOnly(me) && hasClientAssignment(me, activeClientId)));
  const showCreative = !sysadmin && hasAnyPermission(perms, PERM.CREATIVE_INTELLIGENCE_READ);
  const showInfluencers =
    INFLUENCER_SURFACES_ENABLED &&
    !sysadmin &&
    hasAnyPermission(perms, PERM.INFLUENCER_CATALOG_READ);
  const showReports =
    !sysadmin && hasAnyPermission(perms, PERM.REPORT_READ, PERM.REPORT_FINANCE_READ);
  const showCrm = !sysadmin && hasAnyPermission(perms, PERM.CRM_LEAD_READ, PERM.CRM_DEAL_READ);
  const showServices =
    !!me &&
    !sysadmin &&
    hasRoleCategory(me, 'agency') &&
    hasAnyPermission(perms, PERM.BILLING_READ, PERM.CONTRACT_READ);
  const showClientServices =
    !sysadmin && hasAnyPermission(perms, PERM.BILLING_READ, PERM.CONTRACT_READ);
  // Task 303-A-B — the client contract continuity entry point.
  //
  // `me.organization === null` is the load-bearing term, and it is a
  // POSITIVE fact rather than an absence: `organizations_select_same_org`
  // is `id = app.current_org_id()`, and that helper is
  // `app.org_is_operational()`-gated, so the organization row resolves for
  // every actor of an operational tenant and for no actor of a
  // non-operational one. The three empty arrays alone would ALSO match a
  // perfectly healthy actor whose roles had just been revoked — an
  // independent UX review found exactly that — and would have shown them a
  // link only they could see, leading to a refusal.
  //
  // An agency actor of the same suspended tenant never reaches this
  // component: the activation-status boundary ANSWERS agency actors, so
  // `CommercialStandingGate` renders `ActivationPendingPage` instead of the
  // shell. And if one somehow did, the endpoint refuses them — this is a
  // link, not an authority (`SECURITY.md` §9).
  const showContractContinuity =
    !!me &&
    !sysadmin &&
    me.organization === null &&
    me.roles.length === 0 &&
    me.permissions.length === 0 &&
    me.client_assignments.length === 0;
  const clientOnly = roleClientOnly || showContractContinuity;
  // Task 301 (Lane B) — the agency's own Super Agency subscription. Narrower
  // than `showServices` on purpose: `contract.read` does NOT open it, because
  // this is the SaaS subscription rather than the client-facing catalogue.
  const showBilling =
    !!me &&
    !sysadmin &&
    hasRoleCategory(me, 'agency') &&
    hasAnyPermission(perms, PERM.BILLING_READ);
  const showTeam =
    !sysadmin &&
    hasAnyPermission(
      perms,
      PERM.TEAM_READ,
      PERM.TEAM_ROLE_ASSIGN,
      PERM.TEAM_PERMISSION_MANAGE,
      PERM.INVITATION_READ,
      PERM.INVITATION_SEND,
      PERM.INVITATION_CLIENT_USER_MANAGE,
    );
  const showNotifications = !sysadmin && hasAnyPermission(perms, PERM.NOTIFICATION_READ);
  // Task 113 — per-client Client Agency Team (CAT) sidebar shortcut.
  // Mirrors the ClientWorkspaceLayout tab-strip gate exactly
  // (`!me.system_admin && isLeadership(me)`) and the `ClientTeamPage`
  // self-guard. RLS on `client_assignments_select` (Task 007 §6.6)
  // returns the full roster only to leadership / account_manager, so
  // hiding the entry from non-leadership avoids a misleading one-row
  // list; the page's PermissionDeniedState is the load-bearing backstop
  // on direct URL navigation.
  const showClientTeam = !sysadmin && isLeadership(me);
  // Task 071 — agency knowledge library entry. Leadership-only by
  // design; the route guard, RLS, and the page itself all repeat
  // the same `ai.knowledge_pack.manage` gate.
  const showKnowledgeLibrary =
    !!me &&
    !sysadmin &&
    hasRoleCategory(me, 'agency') &&
    hasAnyPermission(perms, PERM.AI_KNOWLEDGE_PACK_MANAGE);

  const clientPath = (suffix: string) =>
    activeClientId ? `/app/clients/${activeClientId}${suffix}` : `/app/clients`;

  // For each client-scoped item, mark "active" only when the URL matches and
  // points at the same client; otherwise the link routes to the active
  // client's tab (or the clients list when no client is selected).
  const clientItemActive = (suffix: string) => {
    if (!activeClientId) return false;
    return location.pathname.startsWith(`/app/clients/${activeClientId}${suffix}`);
  };

  return (
    <aside className="side">
      <a className="side-logo" href="/app">
        super<em>agency</em>
      </a>

      {!sysadmin ? <ClientSwitcher activeClientId={activeClientId} /> : null}

      {/*
        Task 298 (Foundation F) — the operator control plane, appended as
        this lane's single nav entry per the shared-file protocol in
        `tasks/295A-commercial-foundation-and-staging.md` §9.3.

        PLATFORM-ONLY. Gated on `me.system_admin` — the same direct
        platform-role fact the server enforces as the first statement of
        both definer RPCs — and NOT on a permission key, because no
        `platform.*` key exists and one would be unresolvable by
        `app.has_permission()` anyway (295A §6.2). Nav visibility is UX;
        the server refusing a tenant actor is the security property.

        Lane O owns any pre-activation SUPPRESSION of the nav (295A §9.3);
        F adds no standing check here.
      */}
      {sysadmin ? (
        <div className="side-group">
          <SectionLabel>المنصة</SectionLabel>
          <SideItem to="/app/platform/commercial" icon={<SettingsIcon />}>
            الحالة التجارية
          </SideItem>
        </div>
      ) : null}

      {/*
        Agency-section nav. Hidden in toto for client-only actors per the
        client-portal UX rule (AppLanding still auto-redirects /app to the
        actor's single assigned workspace, and personal notifications stay
        reachable via the topbar bell).
      */}
      {!clientOnly ? (
        <div className="side-group">
          <SectionLabel>الوكالة</SectionLabel>
          <SideItem to="/app" end icon={<HomeIcon />}>
            الرئيسية
          </SideItem>
          <SideItem to="/app/clients" icon={<ClientsIcon />} count={clients.length || undefined}>
            العملاء
          </SideItem>
          {showInfluencers ? (
            <SideItem to="/app/influencers" icon={<InfluencersIcon />}>
              المؤثرون
            </SideItem>
          ) : null}
          {showCrm ? (
            <SideItem to="/app/crm" icon={<CrmIcon />}>
              CRM
            </SideItem>
          ) : null}
          {showReports ? (
            <SideItem to="/app/reports" icon={<ReportsIcon />}>
              التقارير
            </SideItem>
          ) : null}
          {/* Task 223 — Creative Library / Creative Intelligence / Paid Media
              are CLIENT-workspace surfaces, not agency-global ones. Their
              agency-section entries were removed; reach them inside a client
              workspace (the "مساحة العميل" section below). */}
          {showServices ? (
            <SideItem to="/app/services" icon={<SettingsIcon />}>
              الخدمات والباقات
            </SideItem>
          ) : null}
          {showNotifications ? (
            <SideItem to="/app/notifications" icon={<BellIcon />}>
              الإشعارات
            </SideItem>
          ) : null}
          {/*
            Task 301B — «اشتراك الوكالة» MOVED OUT OF THIS GROUP into the
            bottom «إدارة» group, where the other org-administration
            surfaces live. The gate expression and the destination are
            unchanged; see the item's own comment down there.
          */}
        </div>
      ) : null}

      {!sysadmin ? (
        <div className="side-group">
          <SectionLabel>
            مساحة العميل
            {activeClient ? (
              <span className="tiny" style={{ marginInlineStart: 6 }}>
                · {activeClient.name}
              </span>
            ) : null}
          </SectionLabel>
          {/*
            Same reason as the agency group above: with no visible
            assignment `clientPath` collapses to `/app/clients`, and a
            stale `kmi:lastClientId` sends the actor to a workspace
            `ClientScopeGate` refuses. Under continuity the ONE item below
            is the whole navigation.
          */}
          {!showContractContinuity ? (
            <SideItem to={clientPath('/overview')} icon={<OverviewIcon />} end={!activeClientId}>
              <span data-active={clientItemActive('/overview')}>نظرة عامة</span>
            </SideItem>
          ) : null}
          {showStrategy ? (
            <SideItem to={clientPath('/strategy')} icon={<StrategyIcon />}>
              الاستراتيجية
            </SideItem>
          ) : null}
          {showContent ? (
            <SideItem to={clientPath('/content')} icon={<ContentIcon />}>
              المحتوى
            </SideItem>
          ) : null}
          {showPublishing ? (
            <SideItem to={clientPath('/publishing')} icon={<PublishIcon />}>
              النشر
            </SideItem>
          ) : null}
          {showPaidMediaClient ? (
            <SideItem to={clientPath('/paid-media')} icon={<PaidMediaIcon />}>
              الإعلانات المدفوعة
            </SideItem>
          ) : null}
          {showCreative ? (
            <SideItem to={clientPath('/creative')} icon={<CreativeIcon />}>
              مكتبة الإبداع
            </SideItem>
          ) : null}
          {/* Task 223 — client-scoped Creative Intelligence entry, mirroring the
              workspace tab. Same CREATIVE_INTELLIGENCE_READ gate as مكتبة الإبداع. */}
          {showCreative ? (
            <SideItem to={clientPath('/creative-intelligence')} icon={<CreativeIcon />}>
              ذكاء الإبداع
            </SideItem>
          ) : null}
          {showInfluencers ? (
            <SideItem to={clientPath('/influencers')} icon={<InfluencersIcon />}>
              المؤثرون
            </SideItem>
          ) : null}
          {showReports ? (
            <SideItem to={clientPath('/reports')} icon={<ReportsIcon />}>
              التقارير
            </SideItem>
          ) : null}
          {showClientServices ? (
            <SideItem to={clientPath('/services')} icon={<SettingsIcon />}>
              الخدمات
            </SideItem>
          ) : null}
          {showContractContinuity ? (
            <SideItem to="/app/contracts" icon={<ReportsIcon />}>
              العقد
            </SideItem>
          ) : null}
          {/*
            Task 113 — per-client CAT (Client Agency Team) shortcut.
            Placed last in the workspace section to mirror the
            ClientWorkspaceLayout tab-strip ordering. The two surfaces
            (sidebar + tab strip) share the same gate (`!sysadmin &&
            isLeadership`) and the same destination route
            (`/app/clients/<id>/team` → `ClientTeamPage`).
          */}
          {showClientTeam ? (
            <SideItem to={clientPath('/team')} icon={<TeamIcon />}>
              الفريق
            </SideItem>
          ) : null}
        </div>
      ) : null}

      <div className="side-group">
        <SectionLabel>إدارة</SectionLabel>
        {showTeam || (leadership && !sysadmin) ? (
          <SideItem to="/app/team" icon={<TeamIcon />}>
            {clientOnly ? 'فريق العميل' : 'الفريق والصلاحيات'}
          </SideItem>
        ) : null}
        {showKnowledgeLibrary ? (
          <SideItem to="/app/knowledge-library" icon={<SettingsIcon />}>
            مكتبة المعرفة
          </SideItem>
        ) : null}
        {/*
          Task 301 (Lane B) — the agency's OWN subscription to Super Agency.
          Task 301B moved it here, from the «الوكالة» group into «إدارة»,
          because it is an organization-administration surface like the team
          roster and the settings page, not a day-to-day operating surface.

          THE GATE IS MOVED VERBATIM — same `showBilling`, same route, same
          label, same icon. The one structural difference is that the
          «الوكالة» group is wrapped in `!clientOnly` and this group is not,
          and that changes nothing: `isClientOnly(me)` returns false whenever
          `hasRoleCategory(me, 'agency')` is true (`permissions.ts:418-422`),
          which `showBilling` already requires. Dropping a condition the
          remaining gate implies is not a widening, and a test pins the
          implication so a future edit to `isClientOnly` cannot break it
          quietly.

          Deliberately labelled «اشتراك الوكالة» and NOT «الفوترة», so it
          cannot be confused with «الخدمات والباقات» — that entry is the
          packages the agency SELLS to its clients, which is a different
          concept entirely (295A §11.B1 / CLP-1.7).

          Nav visibility is UX; the endpoint's 403 is the control. No
          commercial-standing check here — Lane O owns any pre-activation
          suppression of the nav (295A §9.3).
        */}
        {showBilling ? (
          <SideItem to="/app/billing" icon={<SettingsIcon />}>
            اشتراك الوكالة
          </SideItem>
        ) : null}
        <SideItem to="/app/settings" icon={<SettingsIcon />}>
          الإعدادات
        </SideItem>
      </div>

      <div className="side-footer">
        <span className="av">
          {getInitials(me.profile?.full_name ?? me.profile?.email ?? null)}
        </span>
        <span className="side-me">
          <span className="nm">{me.profile?.full_name ?? me.profile?.email ?? 'مستخدم'}</span>
          <span className="rl">
            {sysadmin ? (
              <span className="role-pill">System admin</span>
            ) : (
              me.roles
                .filter((r) => r.category === 'agency' || r.category === 'client')
                .slice(0, 2)
                .map((r) => (
                  <span key={r.key} className="role-pill">
                    {roleLabelAr(r.key)}
                  </span>
                ))
            )}
          </span>
        </span>
        <button
          type="button"
          className="btn-text"
          onClick={() => {
            void signOut();
          }}
          title="تسجيل الخروج"
          style={{ padding: 6 }}
        >
          خروج
        </button>
      </div>
    </aside>
  );
}
